import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Repeat, Undo2 } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/i18n';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { SummaryRow, SummaryRule } from '@/components/ui/detail';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import {
  ACCOUNTING_PERMISSIONS,
  ACCOUNTING_ROUTES,
  type AccountingPostingRow,
  type CanonicalSalePosting,
  type CanonicalPayoutPosting,
  apiMessage,
} from '../accountingApi';
import { deriveStatus, toneForDerivedStatus, toneForDeliveryStatus, toneForKind } from '../lib';

interface Props {
  postingId: string | null;
  onClose: () => void;
  /** A reversal changed the list this dialog was opened from — its rows are stale. */
  onChanged: () => void;
}

/**
 * ONE POSTING, its canonical payload and its live per-installation delivery
 * trail — the operator's answer to "did this reach the ledger, and if not,
 * why".
 *
 * ## Reverse, and why a double-click cannot double-post
 *
 * Reversal is the one destructive, outward-facing control on this screen: it
 * creates a REVERSAL posting that a real connector sends to a real ledger
 * (ADR-0028 §A1, confirmed against the Odoo connector on `feat/real-connectors`,
 * which books it as a debit/credit-swapped `account.move`). Three independent
 * layers stand between a double-click here and a double-post there, and none
 * of them is "the button was disabled fast enough" alone:
 *
 * 1. **`busy` below.** The button disables the instant it is pressed, before
 *    the request resolves — the same pattern `Fiscal/DocumentDetailDialog`
 *    uses for its own reason-bearing actions.
 * 2. **The posting row itself, at the database.**
 *    `AccountingPostingsService.reverse` claims the original with a
 *    compare-and-set (`updateMany({ where: { status: QUEUED }, data: {
 *    status: REVERSED } })`) inside a transaction. Two near-simultaneous
 *    requests both read `QUEUED`, but only one UPDATE matches once Postgres
 *    re-checks the predicate against the row the winner just committed — the
 *    loser gets `claimed.count === 0` and a 400, never a second `REVERSAL`
 *    row. This is DB-enforced, not merely UI-disabled, which is why this
 *    dialog does not additionally invent a client-generated replay key the
 *    way `Fiscal`'s actions do: `ReverseAccountingPostingDto` has no
 *    `operationId` field to send one to, because the compare-and-set already
 *    makes the endpoint safe to call twice.
 * 3. **The delivery and the connector, downstream of that.** Even a second
 *    `REVERSAL` row would still be deduped: `idempotencyKeyFor` is
 *    `posting:<id>`, unique per posting, so `IntegrationDeliveryService`
 *    only ever enqueues one delivery for it; and the Odoo connector calls
 *    `findMoveByRef` before every `create`, so even a retried delivery of
 *    the SAME row is recognised as already posted rather than journaled
 *    twice.
 *
 * A REVERSAL posting cannot itself be reversed (the service throws by name),
 * so the button is withheld for `kind === 'REVERSAL'` rather than left to be
 * discovered as a 400.
 */
export default function PostingDetailDialog({ postingId, onClose, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { prompt } = useConfirm();
  const [busy, setBusy] = useState(false);

  const posting = useApi<AccountingPostingRow>(postingId ? ACCOUNTING_ROUTES.posting(postingId) : null);

  // A reversal mutates the row underneath this dialog; re-read it whenever one
  // succeeds so the badge and the button it offers stay in sync with what the
  // server just decided.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (postingId) posting.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, version]);

  if (!postingId) return null;

  const money = (amount: string, currency: string) => formatCurrency(amount, currency, i18n.language);
  const dateTime = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));

  const data = posting.data;
  const derived = data ? deriveStatus(data) : null;
  const canReverse =
    can(ACCOUNTING_PERMISSIONS.reverse) && data?.status === 'QUEUED' && data?.kind !== 'REVERSAL';

  const reverse = async () => {
    if (!data) return;
    const reason = await prompt({
      title: t('accounting.detail.reverseTitle'),
      body: t('accounting.detail.reverseBody'),
      label: t('accounting.detail.reasonLabel'),
      placeholder: t('accounting.detail.reasonPlaceholder'),
      confirmLabel: t('accounting.detail.reverseConfirm'),
      tone: 'danger',
    });
    if (reason === null) return; // cancelled
    setBusy(true);
    try {
      await api.post(ACCOUNTING_ROUTES.reverse(data.id), reason.trim() ? { reason: reason.trim() } : {});
      toast.success(t('accounting.detail.reversed'));
      setVersion((v) => v + 1);
      onChanged();
    } catch (error) {
      toast.error(apiMessage(error, t('accounting.detail.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={Boolean(postingId)}
      onClose={onClose}
      title={data ? t('accounting.detail.title', { id: data.id.slice(-8) }) : t('accounting.detail.loading')}
      className="max-w-2xl"
    >
      {posting.loading ? (
        <LoadingState />
      ) : posting.error || !data || !derived ? (
        <ErrorState message={posting.error ?? t('accounting.states.loadFailedHint')} />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForKind(data.kind)}>{t(`accounting.kind.${data.kind}`)}</Badge>
            <Badge tone={toneForDerivedStatus(derived)}>{t(`accounting.derivedStatus.${derived}`)}</Badge>
          </div>

          <div className="space-y-1.5">
            <SummaryRow label={t('accounting.detail.amount')}>{money(data.amount, data.currency)}</SummaryRow>
            <SummaryRow label={t('accounting.detail.created')} tone="muted">
              {dateTime(data.createdAt)}
            </SummaryRow>
            {data.reversedAt && (
              <SummaryRow label={t('accounting.detail.reversedAt')} tone="muted">
                {dateTime(data.reversedAt)}
              </SummaryRow>
            )}
            <SummaryRow label={t('accounting.detail.identity')} tone="muted">
              <span className="break-all font-mono text-xs">{data.identity}</span>
            </SummaryRow>
          </div>

          <PayloadSection posting={data} money={money} />

          {/* ---- live delivery status, per installation ---- */}
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Icon as={Repeat} size="sm" />
              {t('accounting.detail.deliveries')}
            </p>
            {data.deliveries.length === 0 ? (
              <p className="text-xs text-ink-muted">{t('accounting.detail.noDeliveries')}</p>
            ) : (
              <ul className="space-y-2">
                {data.deliveries.map((d) => (
                  <li key={d.installationId} className="rounded-lg border p-2 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <Badge tone={toneForDeliveryStatus(d.status)}>
                          {t(`accounting.deliveryStatus.${d.status}`)}
                        </Badge>
                        <span className="font-mono text-ink-muted">{d.installationId.slice(-8)}</span>
                      </span>
                      <span className="text-ink-muted">
                        {d.attempts > 0 && t('accounting.detail.attempts', { count: d.attempts })}
                        {d.deliveredAt && ` · ${dateTime(d.deliveredAt)}`}
                      </span>
                    </div>
                    {d.lastError && <p className="mt-1 text-red-600 dark:text-red-400">{d.lastError}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ---- reverse ---- */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-4">
            {canReverse ? (
              <Button variant="danger" size="sm" onClick={() => void reverse()} disabled={busy}>
                <Icon as={Undo2} />
                {t('accounting.detail.reverse')}
              </Button>
            ) : data.status === 'REVERSED' ? (
              <p className="text-xs text-ink-muted">{t('accounting.detail.alreadyReversed')}</p>
            ) : data.kind === 'REVERSAL' ? (
              <p className="text-xs text-ink-muted">{t('accounting.detail.cannotReverseAReversal')}</p>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** The canonical payload, rendered per `kind` — lines and tenders for a SALE, cash movements for a PAYOUT, both for a REVERSAL (which wraps one of the two, unchanged in magnitude). */
function PayloadSection({
  posting,
  money,
}: {
  posting: AccountingPostingRow;
  money: (amount: string, currency: string) => string;
}) {
  const { t } = useTranslation();
  const payload = posting.payload;
  const inner = payload.kind === 'REVERSAL' ? payload.original : payload;

  return (
    <div className="space-y-3">
      {payload.kind === 'REVERSAL' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-xs dark:border-amber-900 dark:bg-amber-950/30">
          {t('accounting.detail.reversalOf', { kind: t(`accounting.kind.${inner.kind}`) })}
        </div>
      )}

      {inner.kind === 'SALE' ? <SaleLines posting={inner} money={money} /> : <PayoutMovements posting={inner} money={money} />}
    </div>
  );
}

function SaleLines({ posting, money }: { posting: CanonicalSalePosting; money: (a: string, c: string) => string }) {
  const { t } = useTranslation();
  const currency = posting.currency.code;
  return (
    <div className="space-y-3">
      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>{t('accounting.detail.line.description')}</TH>
              <TH>{t('accounting.detail.line.bucket')}</TH>
              <TH className="text-end">{t('accounting.detail.line.net')}</TH>
              <TH className="text-end">{t('accounting.detail.line.tax')}</TH>
              <TH className="text-end">{t('accounting.detail.line.total')}</TH>
            </TR>
          </THead>
          <tbody>
            {posting.lines.map((line) => (
              <TR key={line.lineNumber}>
                <TD className="text-sm">{line.description}</TD>
                <TD className="text-xs text-ink-muted">{t(`accounting.glBucket.${line.glBucket}`)}</TD>
                <TD className="text-end tabular-nums">{money(line.netAmount, currency)}</TD>
                <TD className="text-end tabular-nums">{money(line.taxAmount, currency)}</TD>
                <TD className="text-end tabular-nums font-medium">{money(line.totalAmount, currency)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      <div className="space-y-1">
        <SummaryRow label={t('accounting.detail.totals.net')}>{money(posting.totals.net, currency)}</SummaryRow>
        {Number(posting.totals.discount) !== 0 && (
          <SummaryRow label={t('accounting.detail.totals.discount')} tone="danger">
            -{money(posting.totals.discount, currency)}
          </SummaryRow>
        )}
        <SummaryRow label={t('accounting.detail.totals.tax')}>{money(posting.totals.tax, currency)}</SummaryRow>
        {Number(posting.totals.service) !== 0 && (
          <SummaryRow label={t('accounting.detail.totals.service')}>{money(posting.totals.service, currency)}</SummaryRow>
        )}
        <SummaryRule />
        <SummaryRow label={t('accounting.detail.totals.grand')} tone="accent">
          {money(posting.totals.grand, currency)}
        </SummaryRow>
        {Number(posting.totals.tip) !== 0 && (
          <SummaryRow label={t('accounting.detail.totals.tip')} tone="muted">
            {money(posting.totals.tip, currency)}
          </SummaryRow>
        )}
      </div>

      {posting.tenders.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink-muted">{t('accounting.detail.tenders')}</p>
          <ul className="space-y-1">
            {posting.tenders.map((tender) => (
              <li key={tender.method} className="flex items-center justify-between text-sm">
                <span>{tender.method}</span>
                <span className="tabular-nums">{money(tender.amount, currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PayoutMovements({
  posting,
  money,
}: {
  posting: CanonicalPayoutPosting;
  money: (a: string, c: string) => string;
}) {
  const { t } = useTranslation();
  const currency = posting.currency.code;
  return (
    <div className="space-y-3">
      <SummaryRow label={t('accounting.detail.totals.cashOut')} tone="accent">
        {money(posting.cashOut, currency)}
      </SummaryRow>
      {posting.movements.length > 0 && (
        <ul className="space-y-1">
          {posting.movements.map((m) => (
            <li key={m.id} className="flex items-center justify-between text-sm">
              <span className="text-ink-muted">{m.reason}</span>
              <span className="tabular-nums">{money(m.amount, currency)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
